import { Info } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";
import FastFormTextField from "../../App/Components/Forms/FastFormTextField";
import { FormError, RequiredIndicator } from "../../App/Components/Forms/formHelpers";
import InlineCode from "../../App/Components/Typography/InlineCode";
import { CampaignContact, CampaignTypes } from "../../App/Enums/CampaignTypes";
import { useAppSelector } from "../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../App/State/Slices/SelectedClientSlice";
import { ICampaignFormState } from "../../App/Typings/Campaigns";
import { EmbedScriptInner } from "../../ManageCampaigns/Components/Dialog/EmbedScriptInner";
import { AreaOfInterestTag } from "../../MessageComponents/FunctionMethods/MessageFunctions";
import {
  campaignTypeHelperText,
  getDefaultContactPreference,
  getWelcomeMessageDisplay,
  getWelcomeMessageSubjectDisplay,
  talentNetworkCampaignTypeLabels,
  talentNetworkCampaignTypes,
} from "../Helpers/CampaignConstants";
import { updateFieldsOnCampaignTypeChange, updateFieldsOnContactChange } from "../Helpers/CampaignFunctions";
import { InitialValues } from "../State/FormCreateInitialState";
import WelcomeMessageModal from "./WelcomeMessageModal";

function SaveSuccessDialog({
  submitted,
  createdCampaignId,
  forms,
  campaignName,
}: {
  submitted: boolean;
  createdCampaignId: string;
  forms: any[];
  campaignName: string;
}) {
  const navigate = useNavigate();
  return (
    <Dialog
      open={submitted}
      role={"none presentation"}
      fullWidth
      id="code-snippet"
      PaperProps={{ sx: { maxWidth: "800px" } }}
    >
      <Box sx={{ padding: "10px 20px 10px 20px", maxWidth: "850px" }}>
        <EmbedScriptInner
          campaignName={campaignName}
          campaignId={createdCampaignId}
          forms={forms}
          handleClearDialog={() => {
            navigate("/leadcapture/forms");
          }}
        />
        <Button
          sx={{ ml: "24px" }}
          variant="contained"
          onClick={() => navigate("/leadcapture/forms")}
          data-test="campaign-embed-continue"
        >
          Continue
        </Button>
      </Box>
    </Dialog>
  );
}

function CampaignTypePicker({ formState }: { formState?: ICampaignFormState }) {
  const {
    values: { campaignType, contactPreference, formFields },
    handleChange,
    setFieldValue,
  } = useFormikContext<ICampaignFormState>();
  const { hasCareers } = useAppSelector(selectClientValue);

  const handleChangeCampaignType = (event: SelectChangeEvent<CampaignTypes>) => {
    handleChange(event);
    const newType = event.target.value as CampaignTypes;
    const newContactPreference = getDefaultContactPreference(newType, contactPreference);
    setFieldValue("formFields", updateFieldsOnCampaignTypeChange(formFields, newType, newContactPreference));
    setFieldValue("contactPreference", newContactPreference, true);
  };

  return (
    <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
      <FormLabel htmlFor="campaignType" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Form Type
      </FormLabel>
      <Select
        id="campaignType"
        name="campaignType"
        value={campaignType}
        onChange={handleChangeCampaignType}
        disabled={!hasCareers}
      >
        {talentNetworkCampaignTypes.map((type) => {
          const label = talentNetworkCampaignTypeLabels[type];
          const display = formState?.campaignType === type ? `${label} (Current)` : label;
          return (
            <MenuItem key={type} value={type}>
              {display}
            </MenuItem>
          );
        })}
      </Select>
      <Typography
        variant="caption"
        sx={{ mt: 1, fontStyle: "italic" }}
        data-test={`${campaignType}-description`}
      >
        {campaignTypeHelperText[campaignType]}
      </Typography>
    </Box>
  );
}

function NameInput() {
  const {
    values: { campaignName, campaignType },
    setFieldValue,
    errors,
  } = useFormikContext<ICampaignFormState>();

  const isTalentNetwork = campaignType !== CampaignTypes.Form && campaignType !== CampaignTypes.Event;

  const handleBlur = (val: string) => {
    setFieldValue("campaignName", val, true);
  };

  return (
    <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
      <FormLabel htmlFor="campaign-name" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Form Name
        <RequiredIndicator />
      </FormLabel>
      <FastFormTextField
        id="campaign-name"
        defaultValue={campaignName}
        disabled={isTalentNetwork}
        onBlur={handleBlur}
        maxLength={200}
      />
      <FormError fieldName="campaignName" errors={errors} id="campaign-name-error" />
    </Box>
  );
}

function DateInputs() {
  const {
    values: { campaignType, startDate, endDate, ongoing },
    handleChange,
    setFieldValue,
    errors,
  } = useFormikContext<ICampaignFormState>();

  const isTalentNetwork = campaignType !== CampaignTypes.Form && campaignType !== CampaignTypes.Event;

  const onStartDateChange = (value: string | Date | null, keyboardInputValue?: string | undefined) => {
    setFieldValue("startDate", value);
  };

  const onEndDateChange = (value: string | Date | null) => {
    setFieldValue("endDate", value);
  };

  return (
    <>
      <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
        <FormLabel htmlFor="start-date" sx={{ fontWeight: "bold", mb: 0.5 }}>
          Start Date
          <RequiredIndicator />
        </FormLabel>
        <DatePicker
          disabled={isTalentNetwork}
          value={startDate || null}
          InputProps={{ id: "start-date" }}
          maxDate={ongoing ? "" : endDate}
          renderInput={(props) => <TextField {...props} placeholder="Start Date" />}
          onChange={onStartDateChange}
        />
        <FormError fieldName="startDate" errors={errors} id="start-date-error" />
      </Box>
      <Box sx={{ display: "flex", mb: 0.5, flexDirection: "column" }}>
        <FormLabel htmlFor="end-date" sx={{ fontWeight: "bold", mb: 0.5 }}>
          End Date
          <RequiredIndicator />
          <Tooltip
            sx={{ marginLeft: "3px", padding: "2px", marginTop: "-5px" }}
            title={"Form will end at 12AM on the selected date"}
          >
            <Info fontSize="small" />
          </Tooltip>
        </FormLabel>
        <DatePicker
          disabled={ongoing || isTalentNetwork}
          value={endDate || null}
          minDate={startDate}
          InputProps={{ id: "end-date" }}
          renderInput={(props) => <TextField {...props} placeholder="End Date" />}
          onChange={onEndDateChange}
        />
        <FormError fieldName="endDate" errors={errors} id="end-date-error" />
      </Box>
      <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
        <FormControlLabel
          control={
            <Checkbox
              disabled={isTalentNetwork}
              disableRipple
              style={{ justifyContent: "flex-start" }}
              id="ongoing"
              checked={ongoing}
              onChange={handleChange}
            />
          }
          label={
            <FormLabel htmlFor="ongoing" sx={{ fontWeight: "bold", mb: 1 }}>
              Ongoing?
              <Tooltip
                sx={{ marginLeft: "3px", padding: "2px", marginTop: "-5px" }}
                title={"Form will continue until manually stopped"}
              >
                <Info fontSize="small" />
              </Tooltip>
            </FormLabel>
          }
        />
      </Box>
    </>
  );
}

function ContactPreferencePicker() {
  const {
    values: { campaignType, contactPreference, formFields },
    setFieldValue,
    errors,
  } = useFormikContext<ICampaignFormState>();

  const disabled = campaignType === CampaignTypes.JobAlerts;

  const handleChangeContactPreference = (event: SelectChangeEvent<CampaignContact>) => {
    const newContactPreference = event.target.value as CampaignContact;
    const newFormFields = updateFieldsOnContactChange(
      formFields,
      getDefaultContactPreference(campaignType, newContactPreference),
    );
    setFieldValue("formFields", newFormFields);
    setFieldValue("contactPreference", newContactPreference);
  };

  return (
    <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
      <FormLabel sx={{ fontWeight: "bold", mb: 0.5 }} htmlFor="contactPreference">
        Candidate Contact Preference
        <RequiredIndicator />
      </FormLabel>
      <Select
        name="contactPreference"
        value={contactPreference}
        onChange={disabled ? undefined : handleChangeContactPreference}
        disabled={disabled}
      >
        {campaignType !== CampaignTypes.JobAlertsAndTalentNetwork && (
          <MenuItem value={CampaignContact.Phone}>Phone</MenuItem>
        )}
        <MenuItem value={CampaignContact.Email}>Email</MenuItem>
        <MenuItem value={CampaignContact.EmailAndPhone}>Email & Phone</MenuItem>
      </Select>
      <FormError fieldName="contactPreference" errors={errors} id="contact-preference-error" />
    </Box>
  );
}

function WelcomeMessageInput({
  invalidAreaOfInterestTemplate,
  hasRecruiterTitleTag,
}: {
  invalidAreaOfInterestTemplate: boolean;
  hasRecruiterTitleTag: boolean;
}) {
  const {
    values: { contactPreference, welcomeMessage, welcomeMessageSubject },
    errors,
  } = useFormikContext<ICampaignFormState>();
  return (
    <Box
      sx={{
        display: "flex",
        mb: 1,
        flexDirection: "column",
        position: "relative",
      }}
    >
      <FormLabel sx={{ fontWeight: "bold", mb: 0.5 }} htmlFor="contactPreference">
        Welcome Message
        <RequiredIndicator />
      </FormLabel>
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          width: 1,
          border: "1px solid rgba(0,0,0,0.2)",
          borderColor:
            invalidAreaOfInterestTemplate || hasRecruiterTitleTag ? "error.main" : "rgba(0,0,0,0.2)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        {contactPreference.includes(CampaignContact.Email) ? (
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: "bold" }}>Subject</Typography>
              <Typography style={{ fontStyle: "italic" }}>
                {getWelcomeMessageSubjectDisplay(welcomeMessageSubject)}
              </Typography>
              <FormError fieldName="welcomeMessageSubject" errors={errors} id="welcome-subject-error" />
            </Box>
            <Typography sx={{ fontWeight: "bold" }}>Body</Typography>
            <Typography sx={{ fontStyle: "italic" }}>{getWelcomeMessageDisplay(welcomeMessage)}</Typography>
            <FormError fieldName="welcomeMessage" errors={errors} id="welcome-message-error" />
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontStyle: "italic" }}>{getWelcomeMessageDisplay(welcomeMessage)}</Typography>
            <FormError fieldName="welcomeMessage" errors={errors} id="welcome-message-error" />
          </Box>
        )}
        <WelcomeMessageModal />
      </Box>
      <Collapse in={invalidAreaOfInterestTemplate} unmountOnExit>
        <FormHelperText id="area-of-interest-error" error sx={{ mt: 1 }}>
          To use the <InlineCode>{AreaOfInterestTag}</InlineCode> tag, the "Area of Interest" field cannot be{" "}
          <strong>archived</strong> and must be <strong>enabled</strong>, <strong>required</strong> and{" "}
          <strong>contain at least one option</strong>. Update the form fields or remove the tag from the
          Welcome Message to proceed.
        </FormHelperText>
      </Collapse>
      <Collapse in={hasRecruiterTitleTag} unmountOnExit>
        <FormHelperText id="recruiter-tag-error" error sx={{ mt: 1 }}>
          <InlineCode>[[Recruiter**]]</InlineCode> tags of any kind are not supported in form welcome
          messages. Remove them from the Welcome Message to proceed.
        </FormHelperText>
      </Collapse>
    </Box>
  );
}

function CustomSubmissionInput() {
  const {
    values: { submitMessage, submitButtonText },
    setFieldValue,
    errors,
  } = useFormikContext<ICampaignFormState>();

  return (
    <>
      <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
        <FormLabel htmlFor="submit-message" sx={{ fontWeight: "bold", mb: 0.5 }}>
          Submission Message
          <RequiredIndicator />
          <Tooltip
            sx={{ marginLeft: "3px", padding: "2px", marginTop: "-5px" }}
            title={"The message the user will see after submitting the form."}
          >
            <Info fontSize="small" />
          </Tooltip>
        </FormLabel>
        <FastFormTextField
          multiline={true}
          rows={4}
          id="submit-message"
          defaultValue={submitMessage ?? InitialValues.submitMessage}
          onBlur={(val) => setFieldValue("submitMessage", val, true)}
          maxLength={500}
        />
        <FormError fieldName="submitMessage" errors={errors} id="submit-message-error" />
      </Box>
      <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
        <FormLabel htmlFor="submit-buttom-text" sx={{ fontWeight: "bold", mb: 0.5 }}>
          Submit Button Text
          <RequiredIndicator />
          <Tooltip
            sx={{ marginLeft: "3px", padding: "2px", marginTop: "-5px" }}
            title={'The text of the submit button i.e. "Save", "Submit", "Apply"'}
          >
            <Info fontSize="small" />
          </Tooltip>
        </FormLabel>
        <FastFormTextField
          id="submit-button-text"
          defaultValue={submitButtonText ?? InitialValues.submitButtonText}
          onBlur={(val) => setFieldValue("submitButtonText", val, true)}
          maxLength={50}
        />
        <FormError fieldName="submitButtonText" errors={errors} id="submit-button-text-error" />
      </Box>
    </>
  );
}

function SubmitCancelButtons({
  isSubmitDisabled,
  submitText,
  onCancelClick,
}: {
  isSubmitDisabled: boolean;
  submitText: string;
  onCancelClick: () => void;
}) {
  return (
    <Box sx={{ mt: 2 }}>
      <Button
        disabled={isSubmitDisabled}
        sx={{ width: "fit-content" }}
        variant="contained"
        type="submit"
        id="create-campaign-button"
        size="large"
      >
        {submitText}
      </Button>
      <Button
        sx={{ marginLeft: "10px" }}
        variant="contained"
        type="button"
        id="cancel-campaign-button"
        size="large"
        color="info"
        onClick={onCancelClick}
      >
        Cancel
      </Button>
    </Box>
  );
}

export {
  CampaignTypePicker,
  ContactPreferencePicker,
  CustomSubmissionInput,
  DateInputs,
  NameInput,
  SaveSuccessDialog,
  SubmitCancelButtons,
  WelcomeMessageInput,
};
